/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { ActiveSubscription, Product } from "@/adapters/productsAdapter";
import { License, Order } from "@/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface UserState {
  id: string | null;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: {
    line1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  isAuthenticated: boolean;
  billingDetails: {
    name: string;
    email: string;
    address: {
      line1: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  } | null;
  products: Product[];
  theme: "system" | "light" | "dark";
  invoices: Order[];
  activeSubscriptions: ActiveSubscription[];
  deviceRemovedToggle: boolean;
  licenses: License[];
  newUser: boolean;
  breadpoint: string | null;
  userCurrency: string;
}

const initialState: UserState = {
  id: "",
  name: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: {
    line1: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  },
  isAuthenticated: false,
  billingDetails: null,
  products: [] as Product[],
  theme: "system",
  invoices: [],
  activeSubscriptions: [],
  deviceRemovedToggle: false,
  licenses: [],
  newUser: false,
  breadpoint: null,
  userCurrency: "USD",
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<{ id: string; name: string; email: string }>) {
      state.id = action.payload.id;
      state.name = action.payload.name;
      state.email = action.payload.email;
    },
    setUserCurrency(state, action: PayloadAction<string>) {
      state.userCurrency = action.payload;
    },
    updateUserDetails(
      state,
      action: PayloadAction<{
        name: string;
        email?: string;
        phone?: string;
        address?: Partial<UserState["address"]>;
      }>
    ) {
      const { name, email, phone, address } = action.payload;
      state.name = name;
      const [firstName, ...rest] = name.split(" ");
      state.firstName = firstName || name;
      state.lastName = rest.join(" ") || "";
      state.email = email || state.email;
      state.phone = phone || state.phone;
      if (address) {
        state.address = {
          ...state.address,
          ...address,
        };
      }
    },
    setBreadpoint(state, action: PayloadAction<string | null>) {
      state.breadpoint = action.payload;
    },
    setNewUser(state, action: PayloadAction<boolean>) {
      state.newUser = action.payload;
    },
    clearUser(state) {
      state.id = null;
      state.name = "";
      state.email = "";
      state.isAuthenticated = false;
      state.billingDetails = null;
      state.products = [];
    },
    setAuthentication(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload;
    },
    setDeviceRemovedToggle(state) {
      state.deviceRemovedToggle = !state.deviceRemovedToggle;
    },
    setBillingDetails(state, action: PayloadAction<UserState["billingDetails"]>) {
      state.billingDetails = action.payload;
    },
    setProducts(state, action: PayloadAction<Product[]>) {
      state.products = action.payload;
    },
    setTheme(state, action: PayloadAction<"system" | "light" | "dark">) {
      state.theme = action.payload;
    },
    setInvoices(state, action: PayloadAction<Order[]>) {
      state.invoices = action.payload;
    },
    setActiveSubscriptions(state, action: PayloadAction<ActiveSubscription[]>) {
      state.activeSubscriptions = action.payload;
    },
  },
});

export const {
  setUser,
  clearUser,
  setAuthentication,
  setBillingDetails,
  setProducts,
  setTheme,
  setInvoices,
  setActiveSubscriptions,
  setDeviceRemovedToggle,
  setNewUser,
  setBreadpoint,
  updateUserDetails,
  setUserCurrency,
} = userSlice.actions;
export default userSlice.reducer;
